/**
 * E06-F03-T14 — a human confirms the website of a reference company before the paid
 * AI check reads it.
 *
 * Reference companies (intake competitors) arrive as bare names; their website is
 * found by CODE (the free name search, or a same-name discovery hit). Wave-1 data:
 * 7 of 60 such domains were someone else's or doubtful (Peloton `.co.uk`, Tempo
 * `.co.il`, Parker `.ru`) — the AI check then reads the wrong company. So a
 * code-found reference website is `unconfirmed` until a person either confirms it
 * or types another one in Review.
 *
 * INVARIANTS
 *   - Only a reference row with a known website gets a confirmation state; every
 *     other row is `null` (no mark: discovery / manual-add rows are not in scope).
 *   - A website typed by a person (`evidence.__website === 'manual'`) is confirmed.
 *   - A confirmation is bound to the exact domain it was given for
 *     (`evidence.__websiteConfirmed.domain`): if the website later changes, the row
 *     is unconfirmed again.
 *   - `unconfirmed` WARNS before an AI check; it never blocks it.
 *   - A website typed by a person wins over the library domain on display
 *     (`resolveRowDisplayDomain`) — the AI check already reads `raw_domain` first,
 *     so both screens and the check agree on one website.
 *
 * Pure + DOM-free: the api projects Review / AI-check rows with it, the web shows
 * the labels.
 */
import { z } from 'zod';
import { resolveCompanyDisplayDomain } from './companyDisplay.js';
export const websiteConfirmationSchema = z.enum(['unconfirmed', 'confirmed']);
/** Review mark of a code-found reference website nobody has checked yet. */
export const WEBSITE_UNCONFIRMED_LABEL = 'Domain not confirmed';
/** Review mark once a person confirmed (or typed) the website. */
export const WEBSITE_CONFIRMED_LABEL = 'Domain confirmed';
/** AI-check warning for a reference company whose website nobody confirmed. */
export const AI_RUN_UNCONFIRMED_DOMAIN_WARNING = 'The website of this reference company was found automatically and is not confirmed — the AI check may read another company. Confirm it in Review.';
/** The private evidence key a confirmation is stored under (never serialised). */
export const WEBSITE_CONFIRMED_KEY = '__websiteConfirmed';
function asRecord(value) {
    return value && typeof value === 'object' ? value : null;
}
/** Bare, comparable host: lower-case, no scheme, no `www.`, no path, no trailing dot. */
function comparableHost(value) {
    const host = (value ?? '')
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split(/[/?#]/)[0] ?? '';
    return host.replace(/\.$/, '');
}
/** True when a person typed the row's website in Review (`__website:'manual'`). */
export function hasManualWebsite(evidence) {
    const ev = asRecord(evidence);
    if (!ev)
        return false;
    if (ev.__website === 'manual')
        return true;
    const disc = asRecord(ev.__discovery);
    return disc?.siteLookup === 'manual';
}
/** True for an intake reference company (the discovery seed flag). */
export function isReferenceEvidence(evidence) {
    const disc = asRecord(asRecord(evidence)?.__discovery);
    return disc?.isSeed === true;
}
/**
 * The confirmation state of a row's website: null when the row is not a reference
 * company or has no website; `confirmed` when a person typed it or confirmed THIS
 * domain; `unconfirmed` otherwise (found by code, nobody checked it).
 */
export function deriveWebsiteConfirmation(evidence, domain) {
    const host = comparableHost(domain);
    if (!host || !isReferenceEvidence(evidence))
        return null;
    if (hasManualWebsite(evidence))
        return 'confirmed';
    const mark = asRecord(asRecord(evidence)?.[WEBSITE_CONFIRMED_KEY]);
    if (mark && typeof mark.domain === 'string' && comparableHost(mark.domain) === host) {
        return 'confirmed';
    }
    return 'unconfirmed';
}
/**
 * The website a row shows: a website a person typed (`raw_domain` + manual mark)
 * first — it corrects a wrong library domain for THIS row — otherwise the shared
 * rule (library domain → raw domain).
 */
export function resolveRowDisplayDomain(source, evidence) {
    const typed = source.rawDomain?.trim();
    if (typed && hasManualWebsite(evidence))
        return typed;
    return resolveCompanyDisplayDomain(source);
}
//# sourceMappingURL=referenceWebsite.js.map